// src/components/admin/nornsweave/modals/AddSingleClassModal.js
import React from 'react';
import TimePicker from '../../../TimePicker';
import { GRADE_OPTIONS, SUBJECT_OPTIONS, DURATION_OPTIONS } from '../AdminNornsWeaveContext';

function AddSingleClassModal({ singleClass, setSingleClass, teachers, onClose, onAdd }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Add Single Class</h3>
        <p className="text-slate-600 text-sm mb-4">This is a one-time, non-recurring class visit.</p>
        <div className="space-y-4">
          {/* Teacher Selection from Directory */}
          {teachers.length > 0 && (
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Select from Directory</label>
              <select
                className="w-full px-3 py-2 border border-emerald-300 rounded-lg bg-emerald-50"
                onChange={(e) => {
                  const teacher = teachers.find(t => t.id === e.target.value);
                  if (teacher) {
                    const gradeMap = { 'PreK': 'PreK', 'K': 'K', '1': '1st', '2': '2nd', '3': '3rd', '4': '4th', '5': '5th', '6': '6th', '7': '7th', '8': '8th' };
                    setSingleClass({
                      ...singleClass,
                      teacherName: teacher.name,
                      teacherEmail: teacher.email,
                      grade: gradeMap[teacher.grade] || teacher.grade,
                      subject: teacher.subject || ''
                    });
                  }
                }}
              >
                <option value="">-- Quick select teacher --</option>
                {teachers.sort((a, b) => a.name.localeCompare(b.name)).map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} - {t.grade === 'K' ? 'Kindergarten' : t.grade === 'PreK' ? 'PreK' : `${t.grade}th Grade`}{t.subject ? ` (${t.subject})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date - Required for single class */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-1">Date</label>
            <input
              type="date"
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              value={singleClass.date}
              onChange={(e) => setSingleClass({...singleClass, date: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Teacher Name</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.teacherName}
                onChange={(e) => setSingleClass({...singleClass, teacherName: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Teacher Email</label>
              <input
                type="email"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.teacherEmail}
                onChange={(e) => setSingleClass({...singleClass, teacherEmail: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Grade</label>
              <select
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.grade}
                onChange={(e) => setSingleClass({
                  ...singleClass,
                  grade: e.target.value,
                  subject: ['5th', '6th', '7th', '8th'].includes(e.target.value) ? singleClass.subject : ''
                })}
              >
                <option value="">Select grade</option>
                {GRADE_OPTIONS.map(g => (
                  <option key={g} value={g}>{g} Grade</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Students</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.studentCount}
                onChange={(e) => setSingleClass({...singleClass, studentCount: e.target.value})}
              />
            </div>
          </div>

          {['5th', '6th', '7th', '8th'].includes(singleClass.grade) && (
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Subject</label>
              <select
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.subject || ''}
                onChange={(e) => setSingleClass({...singleClass, subject: e.target.value})}
              >
                <option value="">Select subject</option>
                {SUBJECT_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Start Time"
              value={singleClass.startTime}
              onChange={(val) => setSingleClass({...singleClass, startTime: val})}
              required
            />
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Duration</label>
              <select
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.duration || ''}
                onChange={(e) => {
                  const duration = parseInt(e.target.value);
                  if (singleClass.startTime && duration) {
                    const [hours, mins] = singleClass.startTime.split(':').map(Number);
                    const totalMins = hours * 60 + mins + duration;
                    const endHours = Math.floor(totalMins / 60);
                    const endMins = totalMins % 60;
                    const endTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
                    setSingleClass({...singleClass, duration: e.target.value, endTime});
                  } else {
                    setSingleClass({...singleClass, duration: e.target.value});
                  }
                }}
              >
                <option value="">Select duration</option>
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Library</label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.library}
                onChange={(e) => setSingleClass({...singleClass, library: e.target.value})}
              >
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Booking Type</label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={singleClass.bookingType || 'class'}
                onChange={(e) => setSingleClass({...singleClass, bookingType: e.target.value})}
              >
                <option value="class">Class</option>
                <option value="admin">Admin</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">Admin bookings persist during no-class days</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onAdd}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              Add Single Class
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddSingleClassModal;
